/**
 * Client version policy — GET /versions/dashboard/policy.
 *
 * The endpoint publishes two thresholds so the client can tell
 * "you may keep working" from "you must update before continuing":
 *
 *   current  — newest build deployed        -> optional update notice
 *   minimum  — oldest build still allowed   -> mandatory update block
 *   currentName — display only, NEVER compared
 *
 * Deliberately anonymous (it must answer before login) and never
 * cached: a stale copy would delay a mandatory update reaching users,
 * which is the entire point of the feature. Hence `cache: 'no-store'`
 * on top of the server's own no-store header, and no memoization here.
 *
 * FAIL OPEN. Every failure path returns nulls rather than throwing:
 * a five-second network blip or a misconfigured threshold must never
 * be able to lock a clinician out of a patient chart. Callers block
 * only on an explicit numeric `minimum` their build is genuinely
 * below.
 */
const REQUEST_TIMEOUT_MS = 10000;

const EMPTY_POLICY = { current: null, minimum: null, currentName: null };

// Build numbers are integers like 260831002 (31 Aug 2026, build 002).
// Anything non-numeric — a name, a null, a malformed body — is treated
// as absent, never as zero, so it cannot accidentally satisfy a
// comparison.
export const toBuildNumber = value => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Number(value.trim());
  return Number.isFinite(parsed) ? parsed : null;
};

export const fetchVersionPolicy = async ({ signal } = {}) => {
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT_MS);
  // Caller's signal and our timeout both need to abort the request;
  // AbortSignal.any is not available in every supported browser, so the
  // caller's signal is forwarded manually.
  const onCallerAbort = () => timeoutController.abort();
  signal?.addEventListener('abort', onCallerAbort);

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/versions/dashboard/policy`, {
      method: 'GET',
      cache: 'no-store',
      signal: timeoutController.signal,
    });
    if (!response.ok) return EMPTY_POLICY;

    const body = await response.json();
    const data = body?.data;
    if (!data || typeof data !== 'object') return EMPTY_POLICY;

    return {
      current: toBuildNumber(data.current),
      minimum: toBuildNumber(data.minimum),
      currentName: typeof data.currentName === 'string' ? data.currentName : null,
    };
  } catch {
    // Network error, timeout, abort, unparseable body — all identical
    // from here: behave as if nothing was returned.
    return EMPTY_POLICY;
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener('abort', onCallerAbort);
  }
};

/**
 * Decide what the app should do with a policy response.
 * Returns 'block' | 'notice' | 'none'.
 *
 * The floor is INCLUSIVE: a build equal to `minimum` is still allowed.
 */
export const resolveUpdateAction = (myBuild, policy) => {
  const mine = toBuildNumber(myBuild);
  if (mine === null) return 'none'; // own build unknown -> never block
  if (policy?.minimum !== null && policy?.minimum !== undefined && mine < policy.minimum) {
    return 'block';
  }
  if (policy?.current !== null && policy?.current !== undefined && mine < policy.current) {
    return 'notice';
  }
  return 'none';
};

export default fetchVersionPolicy;
